import { Injectable } from '@angular/core';
import { PersistedAuthData } from '../../features/auth/state-management/auth.state';

const AUTHORIZATION_KEY = 'authorization-token';
const AUTHENTICATION_KEY = 'authentication-token';
const RESOURCES_KEY = 'resources';
const SELECTED_USER_KIND_KEY = 'selectedUserKind';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  save(data: PersistedAuthData): void {
    try {
      sessionStorage.setItem(AUTHORIZATION_KEY, data['authorizationToken']);
      sessionStorage.setItem(AUTHENTICATION_KEY, data['authenticationToken']);
      sessionStorage.setItem(SELECTED_USER_KIND_KEY, data['selectedUserKind']);
    } catch {
      // Silently handle sessionStorage exceptions (private mode, quota exceeded)
    }
  }
  saveResources(data: string[]): void {
    try {
      sessionStorage.setItem(RESOURCES_KEY, JSON.stringify(data));
    } catch {
      // Silently handle sessionStorage exceptions (private mode, quota exceeded)
    }
  }

  load(): PersistedAuthData | null {
    try {
      const authorizationToken = sessionStorage.getItem(AUTHORIZATION_KEY);
      const authenticationToken = sessionStorage.getItem(AUTHENTICATION_KEY);
      const resources = sessionStorage.getItem(RESOURCES_KEY);
      const userKind = sessionStorage.getItem(SELECTED_USER_KIND_KEY);
      if (
        userKind === null ||
        authorizationToken == null ||
        authenticationToken == null ||
        resources == null
      ) {
        return null;
      }

      const parsed: PersistedAuthData = {
        authenticationToken,
        authorizationToken,
        resources: JSON.parse(resources),
        selectedUserKind: userKind,
      };

      if (!this.isValidPersistedAuthData(parsed)) {
        return null;
      }

      return parsed as PersistedAuthData;
    } catch {
      // Malformed JSON or sessionStorage not available
      return null;
    }
  }

  clear(): void {
    try {
      sessionStorage.removeItem(SELECTED_USER_KIND_KEY);
      sessionStorage.removeItem(AUTHORIZATION_KEY);
      sessionStorage.removeItem(AUTHENTICATION_KEY);
      sessionStorage.removeItem(RESOURCES_KEY);
    } catch {
      // Silently handle sessionStorage exceptions
    }
  }

  private isValidPersistedAuthData(obj: unknown): boolean {
    if (obj === null || typeof obj !== 'object') {
      return false;
    }

    const record = obj as Record<string, unknown>;

    return (
      typeof record['authenticationToken'] === 'string' &&
      typeof record['authorizationToken'] === 'string' &&
      Array.isArray(record['resources']) &&
      typeof record['selectedUserKind'] === 'string'
    );
  }
}
